import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SkeletonPlaceholder,
  Button,
  Modal,
  Tabs,
  Tab,
  Tooltip,
  TabList,
  TabPanels,
  TabPanel,
} from '@carbon/react';
import DetailsCard from '../../components/DetailsCard/DetailsCard';
import ServicesTile from './ServicesTile/ServicesTile';
import Header from '../../components/Header/Header';
import DeploymentsTable from '../../components/DeploymentsTable/DeploymentsTable';
import {
  getApplication,
  getDeployments,
  getAllDeploymentInstance,
  deleteApplicationService,
  deleteApplication,
  getApplications,
  getDeploymentServiceEndPoints,
  deleteDeployment,
  deleteDeploymentServiceEndPoint,
} from '../../controllers/applicationApis';
import { getNetworkSegments } from '../../controllers/networksegmentsApi.js';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getDeploymentEnvs,
  getDeploymentEnvSubtypes,
  getPartitions,
} from '../../controllers/deploymentEnv.js';
import {
  ApplicationData,
  Service,
  ServiceData,
  Deployment,
  DeploymentData,
  DeploymentInstance,
  DeploymentInstanceData,
  ResourceGroup,
  DeploymentEnvironment,
  DeploymentEnvironmentSubtype,
  PolicyData,
  Partition,
  NetworkSegment,
  Error500Type,
  Gateway,
  ServiceEndPoint,
  Event,
  Policy,
  AppliedFilter,
} from '../../models/master';
import Notification from '../../components/Notifications/Inline/Notification';
import { NotificationContext } from '../../components/Notifications/Context/NotificationProvider';
import dateUtils from '../../lib/dates';
import { getResourceGroups } from '../../controllers/resourceGroupApi';
import { AxiosError } from 'axios';
import {
  getDeploymentEnv,
  getPartition,
} from '../../controllers/deploymentEnv';
import sortData from '../../lib/tableSort';
import { deletePolicy, getPolicies } from '../../controllers/policyApi';
import useAnalytics from '../../lib/useAnalytics';
import Error500 from '../Errors/Error500';

import './ApplicationDetails.scss';
import analyticsData from '../../lib/analyticsEventData';
import GenericTruncateString from '../../components/GenericTruncateString/GenericTruncateString';
import {
  DEFAULT_NETWORK_SEGMENT_ID,
  defaultNetworkSegmentObject,
} from '../../lib/constants';
import InstancesTable from '../../components/InstancesTable/InstancesTable';
import { getGateways } from '../../controllers/gateawayApis';
import { getAllEvents } from '../../controllers/eventsApis';
import EditApplication from './EditApplication/EditApplication';
import AssociatedPoliciesTable from '../../components/AssociatedPoliciesTable/AssociatedPoliciesTable';
import CreatePolicy from '../ConnectionAccessPolicies/CreateConnectionAccessPolicy/CreatePolicy';
import EditPolicy from '../ConnectionAccessPolicies/EditConnectionAccessPolicy/EditPolicy';
import { manageFiltersFromStorage } from '../../lib/utils';

const defaultPermissionMap = {
  deploymentEnv: true,
};

const ApplicationDetailsPage: React.FC = () => {
  const { t } = useTranslation('applicationDetails');
  const [unmanagedToggle, setUnmanagedToggle] = useState(true);
  const [appDetailsData, setAppDetailsData] = useState<ApplicationData | null>(
    null
  );
  const [applicationsData, setApplicationsData] = useState<
    ApplicationData[] | null
  >(null);
  const [networkSegments, setNetworkSegments] = useState<
    NetworkSegment[] | null
  >(null);
  const [servicesData, setServicesData] = useState<ServiceData[] | []>([]);
  const [serviceEndpointsData, setServiceEndpointsData] = useState<
    ServiceEndPoint[]
  >([]);
  const [deploymentData, setDeploymentData] = useState<DeploymentData[] | null>(
    null
  );
  const [gateways, setGateways] = useState<Gateway[] | null>(null);

  const [allEventsData, setAllEventsData] = useState<Event[] | null>(null);
  const [filteredDeploymentData, setFilteredData] = useState<
    DeploymentData[] | null
  >(null);
  const [deployments, setDeployments] = useState<Deployment[] | null>(null);
  const [depInstanceMap, setDepInstanceMap] = useState<
    Map<string, DeploymentInstanceData[]>
  >(new Map());
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get('appId');
  const [appDataLoading, toggleAppDataLoading] = useState(false);
  const [deploymentDataLoading, toggleDeploymentDataLoading] = useState(false);
  const [policyDataLoading, setPolicyDataLoading] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [notificationData, setNotificationData] = useState<any>({});
  const [resourceGroupsData, setResourceGroupsData] = useState<
    ResourceGroup[] | null
  >(null);
  const [deleteApplicationOpenModal, toggleDeleteApplicationOpenModal] =
    useState(false);

  const [showEditApplication, setShowEditApplication] = useState(false);

  const [deploymentEnvs, setDeploymentEnvs] = useState<
    DeploymentEnvironment[] | null
  >(null);
  const [deploymentEnvSubTypes, setDeploymentEnvSubTypes] = useState<
    DeploymentEnvironmentSubtype[] | null
  >(null);
  const [searchText, setSearchText] = useState('');
  const [sortKey, setSortKey] = useState('');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC' | 'NONE'>(
    'NONE'
  );
  const [policies, setPolicies] = useState<Policy[] | null>(null);
  const [applicationPolicies, setApplicationPolicies] = useState<
    PolicyData[] | null
  >(null);
  const [disableButton, setDisableButton] = useState(false);
  const [clusterNetworkSegmentsMap, setClusterNetworkSegmentsMap] = useState<{
    [key: string]: string[];
  }>({});
  const [permissionMap, setPermissionMap] = useState(defaultPermissionMap);
  const [error500, setError500] = useState<null | Error500Type>(null);
  const [disableDeleteServiceButton, setDisableDeleteServiceButton] =
    useState(false);
  const [partitions, setPartitions] = useState<Partition[] | null>(null);

  const [externalEndpointMap, setExternalEndpointMap] = useState<Map<
    string,
    ServiceEndPoint[]
  > | null>(null);
  const [clusterEndpointMap, setClusterEndpointMap] = useState<Map<
    string,
    ServiceEndPoint[]
  > | null>(null);

  const [instanceData, setInstanceData] = useState<DeploymentInstance[] | null>(
    []
  );

  const [showCreatePolicy, setShowCreatePolicy] = useState(false);
  const [showEditPolicy, setShowEditPolicy] = useState(false);
  const [policyToEdit, setPolicyToEdit] = useState<string>('');
  const [showDeletePolicy, setShowDeletePolicy] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState<PolicyData | null>(null);
  const [filteredInstanceData, setFilterInstanceData] = useState<
    DeploymentInstance[] | []
  >([]);
  const [filterApplied, setFilterApplied] = useState<AppliedFilter[] | []>([]);

  const notification = useContext(NotificationContext);
  const navigate = useNavigate();

  const { trackButtonClicked, pageViewed } = useAnalytics();

  const breadcrumbs = [
    {
      url: '/',
      label: t('home'),
    },
    {
      url: '/applications',
      label: t('appAndServices'),
    },
  ];

  const handleOnChange = (value: boolean) => {
    setUnmanagedToggle(value);
  };

  const deploymentHeaders = [
    {
      header: t('deployments.tableHeaders.gateway'),
      key: 'gateway',
      originalKey: 'gateway',
      sort: true,
      style: { minWidth: '20%' },
    },
    {
      header: t('deployments.tableHeaders.type'),
      key: 'type',
      originalKey: 'type',
      sort: true,
      style: { minWidth: '16%' },
    },
    {
      header: t('deployments.tableHeaders.location'),
      key: 'location',
      originalKey: 'location',
      sort: true,
      style: { minWidth: '16%' },
    },
    {
      header: t('deployments.tableHeaders.instances'),
      key: 'instances',
      originalKey: 'instances',
      sort: true,
      style: { minWidth: '16%' },
    },
    {
      header: 'Service endpoints',
      key: 'serviceEndpoints',
      originalKey: 'serviceEndpoints',
      sort: true,
      style: { minWidth: '16%' },
    },
    {
      header: t('deployments.tableHeaders.lastUpdated'),
      key: 'lastUpate',
      originalKey: 'lastUpate',
      sort: true,
      style: { minWidth: '16%' },
    },
    {
      header: '',
      key: 'delete',
      originalKey: 'delete',
    },
  ];

  const instanceHeaders = [
    {
      header: t('instances.podName'),
      key: 'pod_name',
      originalKey: 'pod_name',
      sort: true,
      style: { minWidth: '10%' },
    },
    {
      header: t('instances.localIP'),
      key: 'ip_address',
      originalKey: 'ip_address',
      sort: true,
      style: { minWidth: '16%' },
    },
    {
      header: t('instances.deploymentType'),
      key: 'deploymentType',
      originalKey: 'deploymentType',
      sort: true,
      style: { minWidth: '16%' },
    },
    {
      header: t('instances.appDeployment'),
      key: 'appDeployment',
      originalKey: 'appDeployment',
      sort: true,
      style: { minWidth: '16%' },
    },
    {
      header: t('instances.lastUpdated'),
      key: 'updated_at',
      originalKey: 'updated_at',
      sort: true,
      style: { minWidth: '16%' },
    },
  ];

  const policyHeaders = [
    {
      header: t('policies.name'),
      key: 'name',
      originalKey: 'name',
      style: { minWidth: '20%' },
    },
    {
      header: t('policies.to'),
      key: 'to_svc_name',
      originalKey: 'to_svc_name',
      style: { minWidth: '20%' },
    },
    {
      header: t('policies.labels'),
      key: 'labels',
      originalKey: 'labels',
      style: { minWidth: '20%' },
    },
    {
      header: t('policies.lastUpdated'),
      key: 'lastUpdated',
      originalKey: 'lastUpdated',
      style: { minWidth: '20%' },
    },
    {
      header: '',
      key: 'delete',
      originalKey: 'delete',
      style: { minWidth: '20%' },
    },
  ];

  const getSelectedNwSegment = () => {
    if (Array.isArray(networkSegments) && appDetailsData?.network_segment_id) {
      if (appDetailsData?.network_segment_id === DEFAULT_NETWORK_SEGMENT_ID) {
        return defaultNetworkSegmentObject;
      }
      return networkSegments.find(
        networkSegment =>
          networkSegment.resource_id === appDetailsData?.network_segment_id
      );
    }

    return null;
  };

  const selectedNetworkSegment = useMemo(
    () => getSelectedNwSegment(),
    [networkSegments, appDetailsData?.network_segment_id]
  );

  const applicationDetailsLabels = () => {
    const labels: any = [];
    if (appDetailsData?.labels) {
      appDetailsData.labels.forEach((el: any) => {
        labels.push(el);
      });
    }
    if (appDetailsData?.discovered_labels) {
      appDetailsData.discovered_labels.forEach((el: any) => {
        labels.push({
          default: true,
          value: el,
        });
      });
    }
    return labels;
  };

  const formatAppDetailsData = (data: ApplicationData | null) => {
    const formattedData = [
      {
        key: 'name',
        value: data?.['name'],
      },
      {
        key: 'lastUpdated',
        value: dateUtils.getUserFriendlyDate(data?.updated_at),
      },
      {
        key: 'networkSegment',
        value:
          data?.network_segment_id === DEFAULT_NETWORK_SEGMENT_ID ? (
            <GenericTruncateString
              str={selectedNetworkSegment?.name ?? '-'}
              tableView={false}
              limit={16}
            />
          ) : (
            <div className='name-column-value'>
              <Link
                className='no-underline-link'
                to={`/networkSegmentDetails?nwSegId=${data?.network_segment_id}`}
              >
                <GenericTruncateString
                  str={selectedNetworkSegment?.name ?? '-'}
                  tableView={false}
                  limit={16}
                />
              </Link>
            </div>
          ),
      },
      {
        key: 'gatewaySet',
        value: selectedNetworkSegment?.compatibility_set
          ? t(selectedNetworkSegment.compatibility_set)
          : '—',
      },
      {
        key: 'appIdentity',
        value: data?.['app_identity'] ? data?.['app_identity'] : '—',
      },
      {
        key: 'resourceGroup',
        value: getResourceGroupName(data?.['resource_group_id']),
      },
      {
        key: 'description',
        value: data?.['description'] ? data?.['description'] : '—',
      },
      {
        key: 'labels',
        value: applicationDetailsLabels(),
      },
    ];

    return formattedData;
  };

  const formatServiceData = (data: Service[]) => {
    const serviceData: ServiceData[] = [];
    data?.map(item => {
      let obj = {
        id: item['resource_id'],
        name: item['name'],
        ports: item['ports'],
        labels: item['labels'] ?? [],
        ip: 'NA',
      };
      serviceData.push(obj);
    });
    return serviceData;
  };

  const formatInstanceData = (data: DeploymentInstance[]) => {
    const instanceData: DeploymentInstanceData[] = [];
    data?.map(item => {
      let obj = {
        id: item['resource_id'],
        globalIp: 'NA',
        ip: item['ip_address'],
        created: item['created_at'],
      };
      instanceData.push(obj);
    });
    return instanceData;
  };

  const fetchDeploymentDependencies = async (
    item: Deployment,
    view = 'all'
  ) => {
    let promiseList = [
      getAllDeploymentInstance(id, item['resource_id'], view),
      getDeploymentEnv(item['depl_env_id']),
      getDeploymentServiceEndPoints(
        item['application_id'],
        item['resource_id']
      ),
    ];

    if (item['depl_env_id'] && item['partition_id']) {
      promiseList.push(getPartition(item['depl_env_id'], item['partition_id']));
    }

    let obj = null;

    try {
      const values = await Promise.all(promiseList).then(values => values);
      obj = {
        item,
        instances: values[0] ?? [],
        env: values[1],
        serviceEndpoints: values[2].svc_endpoints ?? [],
        partition: values[3],
      };
      return obj;
    } catch (error) {
      console.log('there was an error', error);
      return {
        item,
        instances: [],
        env: null,
        serviceEndpoints: [],
        partition: null,
      };
    }
  };

  const formatDeploymentData = (data: Deployment[] | null, view = 'all') => {
    try {
      const depData: any[] = [];
      const insData: DeploymentInstance[] = [];
      const serEndpoints: ServiceEndPoint[] = [];
      const clusterEndpointMap = new Map();
      const externalEndpointMap = new Map();
      if (data != null && data.length > 0) {
        const fetchAllData = async () => {
          try {
            const responses = await Promise.all(
              data?.map(item => fetchDeploymentDependencies(item, view))
            );

            await Promise.all(
              responses.map(res => {
                const depl = res.item;
                let instances = res?.instances;
                let serviceEndpoints = res?.serviceEndpoints;
                let env = res?.env;
                let partition = res?.partition;
                let gateway;
                let gatewayId: string;
                let gatewayName: string;
                if (partition && gateways) {
                  gateway = gateways.find(
                    (gw: Gateway) => gw.resource_id === partition?.gateway_id
                  );
                }
                gatewayId = gateway?.resource_id ?? '';
                gatewayName = gateway?.name ?? '';

                depInstanceMap.set(
                  depl.resource_id,
                  formatInstanceData(instances)
                );

                depData.push({
                  id: depl.resource_id ?? '',
                  environment: {
                    id: env?.resource_id,
                    name: env?.name,
                  },
                  name: env?.name,
                  type: depl.type
                    ? t(`deployments.${depl?.type as string}`)
                    : t('deployments.cluster'),
                  gateway: {
                    id: gateway?.resource_id,
                    name: gateway?.name,
                    envId: env?.resource_id,
                    envName: env?.name,
                    partitionId: partition?.resource_id,
                    partitionName: partition?.name,
                  },
                  location: {
                    id: env.location_id,
                    name: env.location_name,
                    cloudId: env.cloud_id,
                  },
                  envType: env?.type,
                  partition: partition?.name,
                  partitionId: partition?.resource_id,
                  instances: instances?.length ?? 0,
                  site: 'NA',
                  resourceGroup: 'NA',
                  subType: 'NA',
                  serviceEndpoints: serviceEndpoints.svc_endpoints
                    ? serviceEndpoints.svc_endpoints.length
                    : 0,
                  addServiceEndpoint:
                    serviceEndpoints.svc_endpoints?.length === 0 &&
                    depl.type === 'external'
                      ? true
                      : false,
                  lastUpate: dateUtils.getUserFriendlyDate(depl.updated_at),
                  gatewayId: gateway?.resource_id,
                  gatewayName: gateway?.name,
                  delete:
                    depl?.type === 'external'
                      ? (serviceEndpoints.svc_endpoints &&
                          serviceEndpoints.svc_endpoints.length > 0) ||
                        (instances && instances.length > 0)
                        ? false
                        : true
                      : null,
                });
                insData.push(...instances);

                if (serviceEndpoints.length > 0) {
                  serEndpoints.push(
                    ...serviceEndpoints.map((sep: ServiceEndPoint) => ({
                      ...sep,
                      deploymentType: depl?.type,
                      gatewayId,
                      gatewayName,
                    }))
                  );

                  serEndpoints.map((sep: ServiceEndPoint) => {
                    if (depl.type === 'external') {
                      if (!externalEndpointMap.has(sep.service_id)) {
                        externalEndpointMap.set(sep.service_id, []);
                      }
                      externalEndpointMap.set(sep.service_id, [
                        ...externalEndpointMap.get(sep.service_id),
                        sep,
                      ]);
                    } else {
                      if (!clusterEndpointMap.has(sep.service_id)) {
                        clusterEndpointMap.set(sep.service_id, []);
                      }
                      clusterEndpointMap.set(sep.service_id, [
                        ...clusterEndpointMap.get(sep.service_id),
                        sep,
                      ]);
                    }
                  });
                }
              })
            );
            setDeploymentData(depData);
            setInstanceData(insData);
            setServiceEndpointsData(serEndpoints);
            setExternalEndpointMap(externalEndpointMap);
            setClusterEndpointMap(clusterEndpointMap);
          } catch (error) {
            console.log(error);
          } finally {
            toggleDeploymentDataLoading(false);
          }
        };
        fetchAllData();
      } else {
        setDeploymentData([]);
        setInstanceData([]);
        if (deploymentDataLoading) {
          toggleDeploymentDataLoading(false);
        }
      }

      setServiceEndpointsData(serEndpoints);
      setExternalEndpointMap(externalEndpointMap);
      setClusterEndpointMap(clusterEndpointMap);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreatePolicy = async () => {
    fetchPolicies();
  };

  const fetchPolicies = async () => {
    try {
      const policiesData = await getPolicies(false, false);
      setPolicies(policiesData);
      if (policies) {
        setPolicyDataLoading(false);
      }
    } catch (error) {
      setPolicyDataLoading(false);
      const err = error as AxiosError;
      console.log('Error fetching policies');
    }
  };

  const handleAddExternalService = (
    service?: Service,
    deployments?: Deployment[]
  ) => {
    if (service && service !== null) {
      setServicesData((prev: any) => [
        ...prev,
        {
          id: service.resource_id,
          name: service.name,
          ports: service.ports,
          labels: service.labels ?? [],
          ip: 'NA',
        },
      ]);
    }

    if (deployments && deployments !== null) {
      setDeploymentData((prev: any) => [
        ...prev,
        ...deployments.map((depl: Deployment) => ({
          id: depl.resource_id,
          environment: {
            id: depl.depl_env_id,
            name: `${
              deploymentEnvs !== null
                ? deploymentEnvs?.find(
                    (env: DeploymentEnvironment) =>
                      env.resource_id === depl.depl_env_id
                  )?.name
                : ''
            }/${
              partitions !== null
                ? partitions?.find(
                    (part: Partition) => part.resource_id === depl.partition_id
                  )?.name
                : ''
            }`,
          },
          partition: depl.partition_id,
        })),
      ]);
    }
  };

  const handleEditPolicy = (data: Policy) => {
    setShowEditPolicy(false);
    setPolicyToEdit('');
    const updatedPolicies = (applicationPolicies ?? []).map(
      (policy: PolicyData) => {
        if (policy.resource_id === data.resource_id) {
          return {
            ...data,
            id: data.resource_id,
            to_svc_name:
              appDetailsData?.services?.find(
                (svc: Service) => svc.resource_id === data.to.service.service_id
              )?.name ?? '',
            lastUpdated: data.updated_at,
            labels: data.labels ?? [],
          };
        }
        return policy;
      }
    );
    setApplicationPolicies(updatedPolicies as PolicyData[]);
  };

  const confirmDeletePolicy = async () => {
    setPolicyDataLoading(true);
    try {
      await deletePolicy(policyToDelete?.resource_id);
      setApplicationPolicies((prevState: any) =>
        prevState.filter(
          (policy: PolicyData) =>
            policy.resource_id !== policyToDelete?.resource_id
        )
      );
      setShowDeletePolicy(false);

      notification.onTrigger('TOAST', {
        title: t('policies.delete.notificationTitle'),
        subtitle: t('policies.delete.notificationSubtitle', {
          name: policyToDelete?.name,
        }),
      });
      setPolicyToDelete(null);
    } catch (error) {
      const err = error as AxiosError;
      console.log('There was an error deleting your policy');
    } finally {
      setPolicyDataLoading(false);
    }
  };

  const requestDeleteApplication = () => {
    trackButtonClicked(
      analyticsData['Application Details'].events.deleteApp.props,
      analyticsData['Application Details'].events.deleteApp.event
    );
    toggleDeleteApplicationOpenModal(true);
  };

  const handleRemoveService = async (serviceId: string) => {
    try {
      toggleAppDataLoading(true);
      setDisableDeleteServiceButton(true);
      let listOfPromises: any = [];

      serviceEndpointsData?.map((sep: ServiceEndPoint) => {
        if (sep.service_id === serviceId)
          listOfPromises.push(
            deleteDeploymentServiceEndPoint(
              id,
              sep.deployment_id,
              sep.resource_id
            )
          );
      });

      if (listOfPromises.length > 0) {
        await Promise.all(listOfPromises);
      }
      await deleteApplicationService(id, serviceId);
      setServicesData(servicesData.filter(service => service.id !== serviceId));
      setServiceEndpointsData(
        serviceEndpointsData?.filter(
          (sep: ServiceEndPoint) => sep.service_id === serviceId
        )
      );

      fetchData();

      // Trigger success toastbar
      notification.onTrigger('TOAST', {
        title: t('serviceNotificationMessages.successNotificationTitle'),
        subtitle: t('serviceNotificationMessages.removeServiceSuccess'),
      });
    } catch (error: any) {
      console.error(error);
      const err = error as AxiosError;
      const errorMessage: string =
        error.response !== undefined
          ? error.response['customErrorMessage']
          : '';
      let errorTitle: string = '',
        errorSubtitle: string = '';

      if (err.response?.status === 403) {
        errorTitle = `${t('serviceNotificationMessages.authErrorTitle')}`;
        errorSubtitle = t('serviceNotificationMessages.authErrorSubtitle');
      }

      if (err.response?.status !== 403 && errorMessage.length > 0) {
        errorTitle = `${t(
          'serviceNotificationMessages.errorNotificationTitle'
        )}`;
        errorSubtitle = errorMessage;
      }

      if (errorMessage.length === 0) {
        errorTitle = `${t(
          'serviceNotificationMessages.errorNotificationTitle'
        )}`;
        errorSubtitle = t('serviceNotificationMessages.removeServiceFail');
      }

      setNotificationData({
        kind: 'error',
        title: errorTitle,
        subtitle: errorSubtitle,
      });
      setOpenErrorModal(true);

      // We added this project.reject to prevent the modal closing when error occured.
      // But in latest version of '@carbon/ibm-products' the below line is not required and is throwing error.
      // return Promise.reject(() => console.log(error));
    } finally {
      toggleAppDataLoading(false);
      setDisableDeleteServiceButton(false);
    }
  };

  const handleRemoveApplication = async (id: string) => {
    toggleDeleteApplicationOpenModal(false);
    try {
      setDisableButton(true);
      const deleteAppname = appDetailsData?.name;
      toggleAppDataLoading(true);
      await deleteApplication(id);
      // Trigger success toastbar
      notification.onTrigger('TOAST', {
        title: t('applicationNotificationMessages.successNotificationTitle'),
        subtitle: t(
          'applicationNotificationMessages.removeNotificationMessage',
          {
            appNameDelete: deleteAppname,
          }
        ),
      });
      navigate(`/applications`);
    } catch (error: any) {
      console.error(error);
      let errorTitle: string = '',
        errorSubtitle: string = '';
      const err = error as AxiosError;
      const errorMessage: string = error.response['customErrorMessage'];

      if (err.response?.status === 403) {
        errorTitle = `${t('applicationNotificationMessages.authErrorTitle')}`;
        errorSubtitle = t('applicationNotificationMessages.authErrorSubtitle');
      }

      if (err.response?.status !== 403 && errorMessage.length > 0) {
        errorTitle = `${t(
          'applicationNotificationMessages.errorNotificationTitle'
        )}`;
        errorSubtitle = errorMessage;
      }

      if (errorMessage.length === 0) {
        errorTitle = `${t(
          'applicationNotificationMessages.errorNotificationTitle'
        )}`;
        errorSubtitle = t(
          'applicationNotificationMessages.removeApplicationFail'
        );
      }

      setNotificationData({
        kind: 'error',
        title: errorTitle,
        subtitle: errorSubtitle,
      });
      setOpenErrorModal(true);

      // We added this project.reject to prevent the modal closing when error occured.
      // But in latest version of '@carbon/ibm-products' the below line is not required and is throwing error.
      // return Promise.reject(() => console.log(error));
    } finally {
      setDisableButton(false);
      toggleAppDataLoading(false);
    }
  };

  const handleUpdateApplication = (data: ApplicationData) => {
    setAppDetailsData(data);
  };

  const onNotificationClose = (
    evt: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): any => {
    setOpenErrorModal(false);
  };

  const getResourceGroupName = (id: string | undefined) => {
    const resourceGroup =
      id && Array.isArray(resourceGroupsData)
        ? resourceGroupsData!.find(
            resourceGroup => resourceGroup.resource_id === id
          )
        : null;

    return resourceGroup ? resourceGroup.name : '';
  };

  const filterPolicies = () => {
    let formattedPolicies: any[] = [];

    policies?.forEach((policy: Policy) => {
      if (policy.to.service.application_id === appDetailsData?.resource_id) {
        formattedPolicies.push({
          ...policy,
          id: policy.resource_id,
          to_svc_name:
            appDetailsData?.services?.find(
              (svc: Service) => svc.resource_id === policy.to.service.service_id
            )?.name ?? '',
          lastUpdated: policy.updated_at,
          labels: policy.labels ?? [],
        });
      }
    });

    setApplicationPolicies(formattedPolicies);
  };

  const fetchDeploymentsData = async (id: string | null) => {
    let deploymentsData: Deployment[] = await getDeployments(id);
    const gateways = await getGateways();
    setGateways(gateways);
    setDeployments(deploymentsData);
    return deploymentsData;
  };

  const openEditApplicationModal = () => {
    trackButtonClicked(
      analyticsData['Application Details'].events.editApp.props,
      analyticsData['Application Details'].events.editApp.event
    );
    setShowEditApplication(true);
  };

  const handleCloseServiceTearsheet = () => {
    fetchData();
    fetchDeploymentEnvironments();
  };

  const fetchData = async () => {
    try {
      toggleAppDataLoading(true);
      toggleDeploymentDataLoading(true);
      const appData: ApplicationData = await getApplication(
        id,
        false,
        true,
        true
      )
        .then(response => {
          return response;
        })
        .catch(error => {
          const err = error as AxiosError;

          if (err.response?.status === 404) {
            navigate('/404');
          }

          if (err.response!?.status >= 500) {
            setError500(err.response!?.status?.toString() as Error500Type);
          }
        });
      const deploymentsData: any = fetchDeploymentsData(id);
      setAppDetailsData(appData);
      setServicesData(formatServiceData(appData.services));
      toggleDeploymentDataLoading(true);
      formatDeploymentData(deploymentsData);
      getResourceGroups().then(response => {
        setResourceGroupsData(response.resource_groups as ResourceGroup[]);
      });

      const allApplications = await getApplications(false, false);
      setApplicationsData(allApplications);

      const allEvents = await getAllEvents();
      setAllEventsData(allEvents);
    } catch (error) {
      console.error(error);
    } finally {
      toggleAppDataLoading(false);
    }
  };

  const fetchNwSegmentsList = async () => {
    try {
      const response = await getNetworkSegments();
      setNetworkSegments(response);
    } catch (error) {
      console.error(error);
      const err = error as AxiosError;
      if (err.response?.status === 403) {
        console.error(error);
      }
    }
  };

  const onfreshInstanceData = () => {
    setInstanceData(null);
    setSortKey('');
    setSortDirection('NONE');
    fetchData();
    setFilterApplied(manageFiltersFromStorage());
  };

  const fetchClusterNetworkSegments = async (clusterId: string) => {
    try {
      const response = await getPartitions(clusterId, 'managed');

      setPartitions(
        response && response.partitions
          ? (response?.partitions as Partition[])
          : []
      );

      if (Array.isArray(response?.partitions)) {
        for (const namespace of response?.partitions) {
          if (clusterNetworkSegmentsMap[clusterId]) {
            clusterNetworkSegmentsMap[clusterId] = [
              ...clusterNetworkSegmentsMap[clusterId],
              namespace?.network_segment_id,
            ];
          } else {
            clusterNetworkSegmentsMap[clusterId] = [
              namespace?.network_segment_id,
            ];
          }
        }

        setClusterNetworkSegmentsMap(clusterNetworkSegmentsMap);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchDeploymentEnvironments = async () => {
    try {
      const deploymentEnvs: DeploymentEnvironment[] = await getDeploymentEnvs(
        'managed',
        true
      );
      setDeploymentEnvs(deploymentEnvs);
      const deploymentEnvSubTypesData: DeploymentEnvironmentSubtype[] =
        await getDeploymentEnvSubtypes();

      setDeploymentEnvSubTypes(deploymentEnvSubTypesData);

      if (Array.isArray(deploymentEnvs)) {
        for (const deploymentEnv of deploymentEnvs) {
          if (deploymentEnv.type === 'cluster') {
            fetchClusterNetworkSegments(deploymentEnv.resource_id);
          }
        }
      }
    } catch (error: any) {
      toggleDeploymentDataLoading(false);
      if (error.response?.status === 403) {
        setPermissionMap(permissionMap => ({
          ...permissionMap,
          deploymentEnv: false,
        }));
      }
    }
  };

  const refreshDeploymentEnvironments = () => {
    setDeploymentEnvs(null);
    setDeploymentEnvSubTypes(null);
    fetchDeploymentEnvironments();
  };

  const updateAppData = (data: ApplicationData) => {
    setAppDetailsData(data);
    setShowEditApplication(false);
  };

  const applyFilterInstance = (data: any) => {
    if (Array.isArray(instanceData)) {
      setFilterInstanceData(data as DeploymentInstance[] | []);
    }
  };

  const fetchGateways = async () => {
    try {
      const resp = await getGateways('managed');
      setGateways(resp as Gateway[]);
    } catch (error) {
      const err = error as AxiosError;
      console.log('There was an error fetching gateways', err);
    }
  };

  const handleDeleteDeployment = async (deplId: string) => {
    try {
      if (appDetailsData) {
        await deleteDeployment(appDetailsData.resource_id, deplId);
        fetchDeploymentsData(appDetailsData.resource_id);
        fetchData();

        notification.onTrigger('TOAST', {
          title: t('deployments.delete.successTitle'),
          subtitle: t('deployments.delete.successSubtitle'),
        });
      }
    } catch (error) {
      const err = error as AxiosError;
      console.log('There was an error deleting deployment');
    }
  };

  useEffect(() => {
    pageViewed('Application Details');
    toggleDeploymentDataLoading(true);
    fetchData();
    fetchPolicies();
    fetchDeploymentEnvironments();
    fetchNwSegmentsList();
    fetchGateways();
  }, []);

  useEffect(() => {
    formatDeploymentData(deployments);
  }, [deployments]);

  useEffect(() => {
    if (appDetailsData !== null && resourceGroupsData !== null) {
      toggleAppDataLoading(false);
    }
  }, [appDetailsData, openErrorModal, resourceGroupsData]);

  const applySearch = (searchText: string) => {
    if (Array.isArray(deploymentData)) {
      setSearchText(searchText);
      if (searchText === '' || searchText === null)
        setFilteredData(deploymentData);
      else {
        const filteredDataset = deploymentData?.filter(data =>
          data?.environment?.name
            ?.toLowerCase()
            .includes(searchText.toLowerCase())
        );
        setFilteredData(filteredDataset);
      }
    }
  };

  const applySearchInstance = (searchText: string) => {
    if (Array.isArray(instanceData)) {
      setSearchText(searchText);
    }
  };

  const handleOpenCreatePolicy = () => {
    setShowCreatePolicy(true);
  };

  const handleTableSort = (
    data: { id: string; text: string },
    sortDirection: 'ASC' | 'DESC' | 'NONE'
  ) => {
    setSortDirection(sortDirection);
    setSortKey(data.id);
  };

  useEffect(() => {
    if (policies && applicationsData && servicesData) {
      filterPolicies();
    }
  }, [policies, applicationsData, servicesData]);

  if (error500) {
    return <Error500 />;
  }

  return (
    <div className='application-details-container'>
      <Header
        title={appDetailsData ? appDetailsData.name : ''}
        breadcrumbs={breadcrumbs}
        loading={appDataLoading}
        // remove for now, functionality not complete
        //   toggle={{
        //     id: 'managed',
        //     state: unmanagedToggle,
        //     onChange: handleOnChange,
        //     labelA: 'Unmanaged',
        //     labelB: 'Managed',
        //   }}
      />
      <div className='application-details-section'>
        {openErrorModal ? (
          <Notification
            kind={notificationData?.kind}
            subtitle={notificationData?.subtitle}
            title={notificationData?.title}
            onClose={onNotificationClose}
          ></Notification>
        ) : null}
        {appDataLoading ? (
          <div className='tiles'>
            <SkeletonPlaceholder className='app-details-skeleton' />
            <SkeletonPlaceholder className='app-details-skeleton' />
          </div>
        ) : (
          <div className='tiles'>
            <DetailsCard
              data={formatAppDetailsData(appDetailsData)}
              isEditable={true}
              openEditModal={openEditApplicationModal}
              detailsCardName={t('detailsCardName')}
              type={'APPLICATION'}
            />
            {!deploymentDataLoading ? (
              <ServicesTile
                onRegisterService={(
                  serviceData?: Service,
                  deploymentsData?: Deployment[]
                ) => {
                  handleAddExternalService(serviceData, deploymentsData);
                }}
                gateways={gateways}
                deployments={deploymentData}
                deploymentEnvs={deploymentEnvs}
                externalEndpointMap={externalEndpointMap}
                clusterEndpointMap={clusterEndpointMap}
                services={servicesData}
                applicationData={appDetailsData}
                allEventsData={allEventsData}
                applicationId={
                  appDetailsData ? appDetailsData.resource_id : null
                }
                removeService={handleRemoveService}
                onCloseServiceTearsheet={handleCloseServiceTearsheet}
                policiesList={applicationPolicies}
                networkSegment={selectedNetworkSegment}
                disableDeleteServiceButton={disableDeleteServiceButton}
                resourceGroupsData={resourceGroupsData}
              />
            ) : (
              <SkeletonPlaceholder className='app-details-skeleton' />
            )}
          </div>
        )}

        <div className='appDeployments-instances-policies-list-tabs'></div>
        <Tabs>
          <TabList contained>
            <Tab>
              {(deploymentData
                ? searchText !== ''
                  ? sortData(
                      filteredDeploymentData ?? [],
                      sortKey,
                      sortDirection
                    )
                  : sortData(deploymentData ?? [], sortKey, sortDirection)
                : []
              ).length > 0 &&
              !policyDataLoading &&
              !deploymentDataLoading
                ? t('deployments.header.1', {
                    count: deploymentData?.length,
                  })
                : t('deployments.header.0')}
            </Tab>
            <Tab>
              {(instanceData ?? []).length > 0
                ? t('instances.header.1', {
                    count: (instanceData ?? []).length,
                  })
                : t('instances.header.0')}
            </Tab>
            <Tab>
              {(applicationPolicies ?? []).length > 0
                ? t('policies.header.1', {
                    count: (applicationPolicies ?? []).length,
                  })
                : t('policies.header.0')}
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <DeploymentsTable
                headers={deploymentHeaders}
                rows={
                  deploymentData
                    ? searchText !== ''
                      ? sortData(
                          filteredDeploymentData ?? [],
                          sortKey,
                          sortDirection
                        )
                      : sortData(deploymentData ?? [], sortKey, sortDirection)
                    : []
                }
                gateways={gateways}
                appDetailsData={appDetailsData}
                instanceMap={depInstanceMap}
                loading={deploymentDataLoading}
                style={{ borderBottom: 'none' }}
                id={id}
                onSearchCallBack={searchText => applySearch(searchText)}
                fetchDeploymentsData={(id: any) => fetchDeploymentsData(id)}
                onDeleteCallback={handleDeleteDeployment}
                sortRows={(
                  data: { id: string; text: string },
                  direction: 'ASC' | 'DESC' | 'NONE'
                ) => handleTableSort(data, direction)}
                error403Flag={!permissionMap['deploymentEnv']}
              />
            </TabPanel>
            <TabPanel>
              <InstancesTable
                headers={instanceHeaders}
                rows={
                  instanceData
                    ? searchText !== ''
                      ? sortData(instanceData ?? [], sortKey, sortDirection)
                      : sortData(instanceData ?? [], sortKey, sortDirection)
                    : []
                }
                deploymentData={deploymentData}
                appDetailsData={appDetailsData}
                gateways={gateways}
                loading={deploymentDataLoading}
                style={{ borderBottom: 'none' }}
                id={id}
                onSearchCallBack={searchText => applySearchInstance(searchText)}
                fetchDeploymentsData={(id: any) => fetchDeploymentsData(id)}
                filteredDataSet={
                  instanceData
                    ? filterApplied.length > 0
                      ? filteredInstanceData
                      : instanceData
                    : []
                }
                filteredDataCallback={data => {
                  applyFilterInstance(data);
                }}
                selectedFiltersVal={filterApplied as any}
                setFilterApplied={(data: AppliedFilter[]) => {
                  setFilterApplied(data);
                }}
                onTableRefresh={() => onfreshInstanceData()}
                sortRows={(
                  data: { id: string; text: string },
                  direction: 'ASC' | 'DESC' | 'NONE'
                ) => handleTableSort(data, direction)}
                error403Flag={!permissionMap['deploymentEnv']}
              ></InstancesTable>
            </TabPanel>
            <TabPanel>
              <AssociatedPoliciesTable
                loading={policyDataLoading}
                rows={applicationPolicies ?? []}
                headers={policyHeaders}
                applicationData={appDetailsData}
                openCreatePolicy={handleOpenCreatePolicy}
                openEditPolicy={(policy: string) => {
                  setShowEditPolicy(true);
                  setPolicyToEdit(policy);
                }}
                requestDelete={(id: string) => {
                  setPolicyToDelete(
                    applicationPolicies?.find(
                      (policy: PolicyData) => policy.resource_id === id
                    ) ?? null
                  );
                  setShowDeletePolicy(true);
                }}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>

        {servicesData?.length > 0 ||
        (Array.isArray(deploymentData) && deploymentData?.length > 0) ||
        (Array.isArray(policies) &&
          appDetailsData != null &&
          policies.filter(
            policy =>
              policy?.from?.application?.application_id ===
              appDetailsData?.resource_id
          ).length > 0) ? (
          <Tooltip
            className='tooltipDeleteApplication'
            align='right'
            label={t('tooltipDeleteApplicationText') as string}
          >
            <Button
              className='applicationDetailsDeleteDisabled'
              kind='danger--ghost'
            >
              {t('deleteApplication')}
            </Button>
          </Tooltip>
        ) : (
          <Button
            className='applicationDetailsDelete'
            kind='danger--ghost'
            onClick={() => requestDeleteApplication()}
          >
            {t('deleteApplication')}
          </Button>
        )}

        {showEditApplication && (
          <EditApplication
            open={showEditApplication}
            onClose={() => setShowEditApplication(false)}
            applicationData={appDetailsData}
            updateAppData={updateAppData}
            networkSegmentList={networkSegments}
          />
        )}

        {showCreatePolicy && (
          <CreatePolicy
            open={showCreatePolicy}
            onAdd={handleCreatePolicy}
            onClose={() => setShowCreatePolicy(false)}
            resourceGroups={resourceGroupsData}
            policyList={policies}
            mode='appDetailsPage'
            applicationData={appDetailsData}
            networkSegment={networkSegments?.find(
              (ns: NetworkSegment) =>
                ns.resource_id === appDetailsData?.network_segment_id
            )}
            networkSegments={networkSegments}
          />
        )}
        {showEditPolicy && (
          <EditPolicy
            open={showEditPolicy}
            onClose={() => setShowEditPolicy(false)}
            onEdit={handleEditPolicy}
            resourceGroups={resourceGroupsData}
            hasResourceGroupAuth={true}
            policyData={
              applicationPolicies?.find(
                (policy: PolicyData) => policy.resource_id === policyToEdit
              ) ?? null
            }
            policyList={applicationPolicies}
            networkSegment={
              networkSegments?.find(
                (ns: NetworkSegment) =>
                  ns.resource_id === appDetailsData?.network_segment_id
              ) ?? null
            }
            hasNetworkSegmentAuth={true}
          />
        )}

        {showDeletePolicy && (
          <Modal
            className='delete-policy-modal'
            danger
            modalHeading={t('policies.delete.modalHeading', {
              name: policyToDelete?.name,
            })}
            onRequestClose={() => setShowDeletePolicy(false)}
            onRequestSubmit={() => confirmDeletePolicy()}
            primaryButtonText={t('policies.delete.tooltip')}
            secondaryButtonText={t('policies.delete.cancel')}
            open={showDeletePolicy}
            size='sm'
          >
            {t('policies.delete.modalContent', {
              policyName: policyToDelete?.name,
              svcName: policyToDelete?.to_svc_name,
            })}
          </Modal>
        )}
        <Modal
          className='delete-application-modal'
          danger
          modalHeading={t('applicationNotificationMessages.deleteModal.header')}
          onRequestClose={() => toggleDeleteApplicationOpenModal(false)}
          onRequestSubmit={() =>
            appDetailsData &&
            handleRemoveApplication(appDetailsData.resource_id)
          }
          primaryButtonText={t(
            'applicationNotificationMessages.deleteModal.confirm'
          )}
          secondaryButtonText={t(
            'applicationNotificationMessages.deleteModal.cancel'
          )}
          open={deleteApplicationOpenModal}
          size='sm'
          primaryButtonDisabled={disableButton}
        >
          {t('applicationNotificationMessages.deleteModal.body', {
            applicationName: appDetailsData?.name,
          })}
        </Modal>
      </div>
    </div>
  );
};

export default ApplicationDetailsPage;
